import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import * as Yup from "yup";
import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { BasicForm, Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";

function statusColor(status) {
  if (status === 1) return "green";
  if (status === 2) return "gray";
  if (status === 3) return "red";
  return "gray";
}

export default function ConceptualRootItems({ rootId, variety }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [resolvedVariety, setResolvedVariety] = useState(variety);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (variety != null && variety !== "") {
      setResolvedVariety(variety);
      return;
    }
    axios
      .get(`/conceptual-roots/${rootId}/`)
      .then(({ data }) => {
        setResolvedVariety(data.response[0]?.variety);
      })
      .catch((err) => toast.error(err));
  }, [rootId, variety]);

  useEffect(() => {
    fetchItems();
  }, [rootId]);

  useEffect(() => {
    if (resolvedVariety == null || resolvedVariety === "") return;
    fetchFacets();
  }, [resolvedVariety, items]);

  function fetchItems() {
    setLoading(true);
    axios
      .post(`/conceptual-roots/${rootId}/retrieve-conceptual-items/`, {
        status: "entire",
        page: 0,
        page_size: 100,
      })
      .then(({ data }) => {
        setItems(data.response[0]?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function fetchFacets() {
    axios
      .post(`/retrieve-conceptual-item-facets/`, {
        variety: Number(resolvedVariety),
      })
      .then(({ data }) => {
        const rows = Array.isArray(data.response) ? data.response : [];
        setFacets(
          rows.map((row) => ({
            value: row.facet,
            label: `${row.facet} — ${row.label}`,
          }))
        );
      })
      .catch((err) => toast.error(err));
  }

  function onCreate(formData) {
    setCreating(true);
    axios
      .post(`/conceptual-roots/${rootId}/conceptual-items/`, {
        facet: formData.facet,
      })
      .then(() => {
        setCreating(false);
        setShowCreate(false);
        setFormKey((key) => key + 1);
        toast.success(t("created"));
        fetchItems();
      })
      .catch((err) => {
        setCreating(false);
        toast.error(err);
      });
  }

  function toggleActive(itemId) {
    setTogglingId(itemId);
    axios
      .post(
        `/conceptual-roots/${rootId}/conceptual-items/${itemId}/active-toggle/`
      )
      .then(() => {
        setTogglingId(null);
        toast.success(t("updated"));
        fetchItems();
      })
      .catch((err) => {
        setTogglingId(null);
        toast.error(err);
      });
  }

  const createFields = [
    {
      name: "facet",
      label: "Facet",
      initialValue: "",
      required: true,
      select: true,
      options: facets,
      disabled: !facets.length,
      schema: () => Yup.string().required(t("validation:required")),
    },
  ];

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Can permission={PERMISSIONS.CONCEPTUAL_ITEMS_CREATE}>
        {!showCreate ? (
          <Group>
            <Button variant="light" onClick={() => setShowCreate(true)}>
              {t("create")} {t("conceptual_item_singular")}
            </Button>
          </Group>
        ) : (
          <Card>
            <Group justify="space-between" mb="sm">
              <Title order={4}>
                {t("create")} {t("conceptual_item_singular")}
              </Title>
              <Button
                size="xs"
                variant="subtle"
                onClick={() => setShowCreate(false)}
              >
                {t("cancel")}
              </Button>
            </Group>
            <BasicForm
              key={`${formKey}-${resolvedVariety}-${facets.length}`}
              buttonProps={{ fullWidth: true }}
              loading={creating}
              onSubmit={onCreate}
              fields={createFields}
            />
          </Card>
        )}
      </Can>

      {!items.length && <EmptyMessage />}
      {items.map((item) => (
        <Card key={item.id}>
          <Group justify="space-between" align="flex-start">
            <div>
              <Text
                component={Link}
                to={`/conceptual-roots/${rootId}/items/${item.id}`}
                c="dark"
                fw={700}
              >
                {item.facet_formatted || item.label || item.facet}
              </Text>
              {item.label && item.facet_formatted !== item.label && (
                <Text size="sm" c="dimmed">
                  {item.label}
                </Text>
              )}
            </div>
            <Group gap="xs">
              <Badge variant="light" color={statusColor(item.status)}>
                {item.status_formatted || t("status")}
              </Badge>
              <Can permission={PERMISSIONS.CONCEPTUAL_ITEMS_EDIT}>
                <Button
                  size="xs"
                  variant="light"
                  loading={togglingId === item.id}
                  onClick={() => toggleActive(item.id)}
                >
                  {t("activation_status")}
                </Button>
              </Can>
            </Group>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}
